import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRemoveGroupMemberMutation } from "../../infrastructure/api/membersGroupsApi";
import type { Group, Member } from "../../domain/entities/Member";

interface Props {
  group: Group;
  /** The member to remove; the dialog is open while this is set. */
  member: Member | null;
  onClose: () => void;
}

/** Confirms and removes one member from a group (not from the institution). */
export const RemoveGroupMemberDialog: React.FC<Props> = ({ group, member, onClose }) => {
  const { t } = useTranslation("membersGroups");
  const [removeGroupMember, { isLoading }] = useRemoveGroupMemberMutation();

  const handleConfirm = async () => {
    if (!member) return;
    try {
      await removeGroupMember({ groupId: group.id, memberUserId: member.id }).unwrap();
      toast.success(
        t("removeGroupMemberDialog.success", { name: member.name, group: group.name }),
      );
      onClose();
    } catch (error) {
      const data = (error as { data?: { message?: string; error?: { message?: string } } })
        ?.data;
      toast.error(
        data?.error?.message || data?.message || t("removeGroupMemberDialog.error"),
      );
    }
  };

  return (
    <Dialog
      open={!!member}
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
    >
      <DialogContent className="rounded-xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("removeGroupMemberDialog.title")}
          </DialogTitle>
          <p className="mt-1 text-sm text-gray-500">
            {t("removeGroupMemberDialog.confirm", {
              name: member?.name ?? "",
              group: group.name,
            })}
          </p>
        </DialogHeader>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("removeGroupMemberDialog.cancel")}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            loading={isLoading}
            className="bg-[#E53935] hover:bg-[#E53935] text-white px-4 py-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("removeGroupMemberDialog.remove")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
